import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { getRequestAsync, postRequestAsync, putRequestAsync } from '../../helpers/apiHandler';
import axios from 'axios';
import config from '../../config/Constant';
import CustomAlert from '../../components/CustomAlert';
import searchIcon from '../../assets/SidebarIcons/icon-search.svg';
import plusIcon from '../../assets/SidebarIcons/icon-plus.svg';
import WhatsAppManagerDataTable from '../DataTables/WhatsAppManagerDataTable';
import WhatsAppManagerModal from './WhatsAppManagerModal';
import WhatsAppManagerDeleteModal from './WhatsAppManagerDeleteModal';
import './_userManagement.scss';

// Helper function for authenticated delete requests
const deleteRequestWithAuth = async (url, authToken) => {
  try {
    const headers = authToken ? { Authorization: authToken } : {};
    const response = await axios.delete(`${config.urls.api_base_url}${url}`, { headers });
    return {
      success: true,
      error: false,
      message: response.data.message || 'Deleted successfully',
      data: response.data.data,
      status_code: response.status
    };
  } catch (error) {
    return {
      success: false,
      error: true,
      message: error.response?.data?.message || error.message || 'Delete failed',
      status_code: error.response?.status || 500
    };
  }
};

const WhatsAppManagerManagement = () => {
  const { access_token } = useSelector((state) => state.auth);
  const [loadingData, setLoadingData] = useState(false);
  const [search, setSearch] = useState('');
  const [data, setData] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedManager, setSelectedManager] = useState({});
  const [availableUsers, setAvailableUsers] = useState([]);
  const [focus, setFocus] = useState(false);

  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'danger',
    message: '',
  });

  const closeModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedManager({});
  };

  const openModal = (mode, row) => {
    if (mode === 'add') {
      setShowAddModal(true);
      return;
    }
    if (mode === 'edit') {
      setSelectedManager(row);
      setShowEditModal(true);
      return;
    }
    if (mode === 'delete') {
      setSelectedManager(row);
      setShowDeleteModal(true);
      return;
    }
  };

  const getWhatsAppManagerData = async () => {
    setLoadingData(true);
    try {
      const response = await getRequestAsync('/admin/whatsapp-managers', {
        headers: { Authorization: access_token.token }
      });

      if (response.success) {
        console.log('API response data:', response.data);
        setData(response.data || []);
        setSearchData(response.data || []);
      } else {
        setAlertState({
          show: true,
          variant: 'danger',
          message: response.message || 'Failed to fetch WhatsApp managers'
        });
      }
    } catch (error) {
      console.error('Error fetching WhatsApp managers:', error);
      setAlertState({
        show: true,
        variant: 'danger',
        message: 'Error fetching WhatsApp managers'
      });
    } finally {
      setLoadingData(false);
    }
  };

  const getAvailableUsers = async () => {
    try {
      const response = await getRequestAsync('/admin/user-management', {
        headers: { Authorization: access_token.token }
      });

      if (response.success) {
        // Filter users with 'manager' role, active status, and not already WhatsApp managers
        const allUsers = response.data || [];
        const activeUsers = allUsers.filter(user => user.active === true);

        const usersWithManagerRole = activeUsers.filter(user => user.role === 'manager');

        const managerUsers = usersWithManagerRole.filter(user =>
          !data.some(manager => manager.id === user.id)
        );
        setAvailableUsers(managerUsers);
      }
    } catch (error) {
      console.error('Error fetching available users:', error);
    }
  };

  const handleCreateManager = async (managerData) => {
    try {
      const response = await postRequestAsync('/admin/whatsapp-managers', managerData, access_token.token);

      if (response.success) {
        setAlertState({
          show: true,
          variant: 'success',
          message: 'WhatsApp manager created successfully'
        });
        getWhatsAppManagerData();
        closeModal();
      } else {
        console.error('API response error:', response);
        console.error('Response status:', response.status_code);
        console.error('Response message:', response.message);
        setAlertState({
          show: true,
          variant: 'danger',
          message: response.message || 'Failed to create WhatsApp manager'
        });
      }
    } catch (error) {
      console.error('Error creating WhatsApp manager:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      setAlertState({
        show: true,
        variant: 'danger',
        message: 'Error creating WhatsApp manager'
      });
    }
  };

  const handleUpdateManager = async (managerData) => {
    try {
      const response = await putRequestAsync(`/admin/whatsapp-managers/${selectedManager.id}`, managerData, access_token.token);

      if (response.success) {
        setAlertState({
          show: true,
          variant: 'success',
          message: 'WhatsApp manager updated successfully'
        });
        getWhatsAppManagerData();
        closeModal();
      } else {
        console.error('API response error:', response);
        console.error('Response status:', response.status_code);
        console.error('Response message:', response.message);
        setAlertState({
          show: true,
          variant: 'danger',
          message: response.message || 'Failed to update WhatsApp manager'
        });
      }
    } catch (error) {
      console.error('Error updating WhatsApp manager:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      setAlertState({
        show: true,
        variant: 'danger',
        message: 'Error updating WhatsApp manager'
      });
    }
  };

  const handleDeleteManager = async () => {
    try {
      const response = await deleteRequestWithAuth(`/admin/whatsapp-managers/${selectedManager.id}`, access_token.token);

      if (response.success) {
        setAlertState({
          show: true,
          variant: 'success',
          message: 'WhatsApp manager removed successfully'
        });
        getWhatsAppManagerData();
        closeModal();
      } else {
        setAlertState({
          show: true,
          variant: 'danger',
          message: response.message || 'Failed to remove WhatsApp manager'
        });
      }
    } catch (error) {
      console.error('Error removing WhatsApp manager:', error);
      setAlertState({
        show: true,
        variant: 'danger',
        message: 'Error removing WhatsApp manager'
      });
    }
  };

  const handleActivateManager = async (manager) => {
    try {
      const response = await putRequestAsync(
        `/admin/whatsapp-managers/${manager.id}`,
        { is_active: true },
        access_token.token
      );

      if (response.success) {
        setAlertState({
          show: true,
          variant: 'success',
          message: 'WhatsApp manager activated successfully'
        });
        getWhatsAppManagerData();
      } else {
        console.error('API response error:', response);
        setAlertState({
          show: true,
          variant: 'danger',
          message: response.message || 'Failed to activate WhatsApp manager'
        });
      }
    } catch (error) {
      console.error('Error activating WhatsApp manager:', error);
      setAlertState({
        show: true,
        variant: 'danger',
        message: 'Error activating WhatsApp manager'
      });
    }
  };

  const handleSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    setSearch(searchTerm);

    if (searchTerm === '') {
      setSearchData(data);
    } else {
      const filtered = data.filter(manager =>
        manager.name?.toLowerCase().includes(searchTerm) ||
        manager.email?.toLowerCase().includes(searchTerm) ||
        manager.manager_type?.toLowerCase().includes(searchTerm)
      );
      setSearchData(filtered);
    }
  };

  useEffect(() => {
    getWhatsAppManagerData();
  }, []);

  useEffect(() => {
    if (showAddModal) {
      getAvailableUsers();
    }
  }, [showAddModal, data]);

  useEffect(() => {
    if (alertState.show) {
      setTimeout(() => {
        setAlertState({
          message: '',
          show: false,
          variant: '',
        });
      }, config.notify_time);
    }
  }, [alertState]);

  return (
    <div className="w-100">
      <div className="page-heading admin-heading">
        <div className="container-left" style={{ marginBottom: "32px" }}>
          <div className="page-title">WhatsApp Manager Management</div>
        </div>
        <div className="manager-user-management-heading">
          <div className='container-left w-100'>
            <InputGroup className="datatable-search search-input">
              <InputGroup.Append className={focus ? 'input-focus' : ''}>
                <InputGroup.Text>
                  <img alt="..." src={searchIcon} />
                </InputGroup.Text>
              </InputGroup.Append>
              <Form.Control
                placeholder="Search managers..."
                name="search"
                value={search}
                onChange={handleSearch}
                onFocus={() => setFocus(true)}
                onBlur={() => setFocus(false)}
                className="search-form"
              />
            </InputGroup>
          </div>
          <div className="container-right mt-0">
            <Button
              variant="primary"
              style={{ minWidth: 160 }}
              onClick={() => openModal('add')}
            >
              <img alt="..." src={plusIcon} />
              Assign Manager
            </Button>
          </div>
        </div>
      </div>
      <CustomAlert
        message={alertState.message}
        show={alertState.show}
        variant={alertState.variant}
        closeAlert={() => setAlertState({ show: false, variant: 'danger', message: '' })}
      />
      <WhatsAppManagerDataTable
        data={searchData}
        loading={loadingData}
        onEdit={(row) => openModal('edit', row)}
        onDelete={(row) => openModal('delete', row)}
        onActivate={handleActivateManager}
      />

      {/* Modals */}
      <WhatsAppManagerModal
        show={showAddModal}
        mode="add"
        availableUsers={availableUsers}
        onClose={closeModal}
        onSubmit={handleCreateManager}
      />

      <WhatsAppManagerModal
        show={showEditModal}
        mode="edit"
        manager={selectedManager}
        onClose={closeModal}
        onSubmit={handleUpdateManager}
      />

      <WhatsAppManagerDeleteModal
        show={showDeleteModal}
        manager={selectedManager}
        onClose={closeModal}
        onConfirm={handleDeleteManager}
      />
    </div>
  );
};

export default WhatsAppManagerManagement;
